

import React, { memo } from 'react';

import cx from 'classnames';
import cxs from 'cxs';
import _omit from 'lodash.omit';
import { COLOURS } from '../../../common/styles/vars';
import Icon from '../../Icon';
import ErrorIcon from '../../ErrorIcon';
import { INPUT_CONTAINER } from '../styles';
import '../styles/_input-container.scss';

function getOptionalStyles({ if: condition, styles }) {
  return condition ? styles : {};
}

const CONTAINER_CORE = {
  border: `1px solid ${COLOURS.grey.mischka.russian}`,
  borderRadius: '4px',
  borderWidth: '1px',

  // Input implementation is different depending on autogrow
  '> input': {
    padding: '0 20px'
  },
  '> * > input': {
    padding: '0 20px'
  },

  ':hover:not(:focus):not(:focus-within) ': {
    borderColor: COLOURS.grey.regent
  },

  ':focus': {
    borderColor: COLOURS.grey.darkDenim
  },

  ':focus-within': {
    borderColor: COLOURS.grey.darkDenim
  }
};

const DECORATOR_STYLES = ({ hasPrefix, hasSuffix, type, invalid, disabled }) => ({
  ...(hasPrefix ? {
    [` ~ .input-container__decorator${hasSuffix ? ':first-of-type' : ''}`]: {
      ...(type === 'SECONDARY' ? {
        borderColor: 'transparent',
        background: COLOURS.grey.athens.parthenon
      } : {}),

      ...(disabled ? {
        background: COLOURS.grey.athens.parthenon,
        color: COLOURS.grey.nevada,
        cursor: 'default',
        pointerEvents: 'none',
        borderColor: 'transparent !important'
      } : {}),

      ...(invalid ? {
        borderColor: `${COLOURS.red.thunderBird} !important`
      } : {})
    }
  } : {}),

  ...(hasSuffix ? {
    [` ~ .input-container__decorator${hasPrefix ? ':last-of-type' : ''}`]: {
      borderColor: COLOURS.grey.mischka.russian,

      ...(type === 'SECONDARY' ? {
        borderColor: 'transparent',
        background: COLOURS.grey.athens.parthenon
      } : {}),

      ...(disabled ? {
        background: COLOURS.grey.athens.parthenon,
        color: COLOURS.grey.nevada,
        cursor: 'default',
        pointerEvents: 'none',
        borderColor: 'transparent !important'
      } : {}),

      ...(invalid ? {
        borderColor: `${COLOURS.red.thunderBird} !important`
      } : {})
    }
  } : {}),

  ':hover:not(:focus) ~ .input-container__decorator': {
    borderColor: COLOURS.grey.regent,

    ...(type === 'SECONDARY' ? {
      background: `${COLOURS.grey.mischka.russian} !important`,
      borderColor: 'transparent'
    } : {}),

    ...(disabled ? {
      background: COLOURS.grey.athens.parthenon,
      color: COLOURS.grey.nevada,
      cursor: 'default',
      pointerEvents: 'none',
      borderColor: 'transparent !important'
    } : {}),

    ...(invalid ? {
      borderColor: `${COLOURS.red.thunderBird} !important`
    } : {})
  },

  ':focus ~ .input-container__decorator': {
    borderColor: `${COLOURS.grey.darkDenim} !important`,

    ...(type === 'SECONDARY' ? {
      background: `${COLOURS.grey.mischka.russian} !important`,
      borderColor: 'transparent'
    } : {}),

    ...(disabled ? {
      background: COLOURS.grey.athens.parthenon,
      color: COLOURS.grey.nevada,
      cursor: 'default',
      pointerEvents: 'none',
      borderColor: 'transparent !important'
    } : {}),

    ...(invalid ? {
      borderColor: `${COLOURS.red.thunderBird} !important`
    } : {})
  }
});

const SECONDARY = {
  borderColor: 'transparent !important',
  backgroundColor: `${COLOURS.grey.athens.parthenon} !important`,

  ':hover': {
    backgroundColor: `${COLOURS.grey.mischka.russian} !important`,
    borderColor: 'transparent'
  },

  ':focus-within': {
    backgroundColor: `${COLOURS.grey.mischka.russian} !important`,
    borderColor: 'transparent'
  }
};

const DISABLED = {
  background: `${COLOURS.grey.athens.parthenon} !important`,
  cursor: 'default',
  pointerEvents: 'none'
};

const INVALID = {
  borderColor: `${COLOURS.red.thunderBird} !important`
};





























function focusInput(e) {
  e.preventDefault();
  e.stopPropagation();

  const input = document.querySelector('.input__element');

  /* istanbul ignore else */
  if (input) {
    input.focus();
  }
}

function getDecorator(
decorator,
/* istanbul ignore next */
{
  isSuffix,
  isErrorIcon,
  iconId,
  errorMessage,
  prefixCanTakeFocus,
  disabled,
  readOnly
} = {},
onClick)
{
  if (!decorator) {return null;}

  const isCustomDecorator = typeof decorator !== 'string';

  /* istanbul ignore next */
  if (isCustomDecorator && !isErrorIcon && prefixCanTakeFocus) {
    return <div className="input-container__decorator">{decorator}</div>;
  }

  const decoratorToRender = typeof decorator === 'string' ?
  <Icon color={disabled || readOnly ? 'nevada' : 'denim'} name={decorator} size={15} onClick={onClick} /> :
  decorator;

  const rendered = isErrorIcon && !onClick ?

  <ErrorIcon
    id={iconId}
    errorMessage={errorMessage}
    tooltipProps={{ place: 'left' }} /> :


  decoratorToRender;

  return (
    <div
      className={cx(
        'input-container__decorator',
        { 'input-container__decorator--is-suffix': isSuffix }
      )}>

      <button
        className="input-container__decorator__button"
        type="button"
        tabIndex={-1}
        onMouseDown={focusInput}
        onClick={focusInput}>

        {rendered}
      </button>
    </div>);

}

/**
 * A component that renders an Input child in a container with prefix/suffix icons
 *
 * @param {InputContainerProps} props
 *
 * @return {React.Element}
 */
function InputContainer({
  children,
  className,
  prefix,
  suffixIcon,
  suffixIconOnClick,
  type = 'PRIMARY',
  valid,
  errorMessage,
  id,
  prefixCanTakeFocus,
  multiline,
  disabled,
  autoGrow,
  hideArrows,
  readOnly
}) {
  const prefixDecorator = getDecorator(
    prefix,
    { iconId: id, prefixCanTakeFocus, disabled, readOnly }
  );
  const suffixDecorator = getDecorator(
    suffixIcon,
    { isSuffix: true, isErrorIcon: !valid, iconId: id, errorMessage, disabled },
    suffixIconOnClick
  );
  const hasPrefix = Boolean(prefix);
  const hasSuffix = Boolean(suffixIcon);
  return (
    <div
      className={`${cx(
        'input-container',
        {
          'input-container--multiline': multiline,
          'input-container--grow': autoGrow,
          'input-container--with-prefix': Boolean(prefixDecorator),
          'input-container--with-suffix': Boolean(suffixDecorator),
          'input-container__hide-arrows': hideArrows
        },
        INPUT_CONTAINER,
        className
      )} ${cxs({
        ...CONTAINER_CORE,
        ...getOptionalStyles({
          if: hasPrefix || hasSuffix,
          styles: DECORATOR_STYLES({ hasPrefix, hasSuffix, type, invalid: !valid, disabled })
        }),
        ...getOptionalStyles({ if: type === 'SECONDARY', styles: SECONDARY }),
        ...getOptionalStyles({ if: readOnly, styles: _omit(DISABLED, 'pointerEvents') }),
        ...getOptionalStyles({ if: disabled, styles: DISABLED }),
        ...getOptionalStyles({ if: !valid, styles: INVALID })
      })}`}>

      {children}
      {prefixDecorator}
      {suffixDecorator}
    </div>);

}

export default memo(InputContainer);